import { getEscHandler } from "@/app/esc_handler"
import { mainLogger as log } from "@/app/logger"
import { useSelector } from "@/app/providers/selector_provider"
import { isArrayWithItems, isFunction, isNotNull, isNull } from "@/app/utils"
import { PopupComponent } from "@/components/selector/popup"
import { ReactNode, useCallback, useEffect, useState } from "react"

export type Item = {
  key: string // sent in the onSelect() callback
  text: string // displayed on the UI
  infoText?: string // optional (i) icon with text
  disabled?: boolean // rendered in grey (not clickable)
}

export type ItemGroup = {
  key: string // key for this group
  text: string // displayed in the UI
  items: Item[] // items rendered under this group
  disabled?: boolean // disables the header AND all items in this group (overrides item.disabled)
}

export type SelectorProps = {
  selectedKey?: string // match the item.key of current selection (optional)
  selectedGroup?: string // match the group.key of the current selection (optional)
  options: ItemGroup[] // to be displayed in the UI, if only 1 group, then header is not shown
  onSelect: (itemKey: string | null, groupKey?: string | null) => void // callback with the key of the selected Item and (optionally) Group (or null if closed)
  header?: ReactNode // optional header to be displayed at the top of the UI
  footer?: ReactNode // optional footer to be displayed at the bottom of the UI
}

// shows a list or an accordian either at the top of the bottom of the viewport
//  - if selectedKey is given, then it will be highlighted in the accordian
//  - onSelect(NULL), if the user closes without a selection (ESC key)
//  - onSelect(selectedKey), if the user selects the same item
//  - if options has only one group, then groupText is NOT shown
export function Selector(props: { config: SelectorProps }) {
  const { options, selectedGroup, selectedKey, onSelect, footer, header } =
    props.config
  log.debug("Rendering Selecter with", { options, selectedGroup, selectedKey })

  // hooks
  const { showSelector } = useSelector()

  // used to store the state between re-renders as the user clicks on accordian headers
  // TODO - maybe use the DOM API to add/remove classname instead of re-rendering via React
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null)

  // setup a new handler which will close the Accordian and invoke the callback
  const close = useCallback(
    (itemKey: string | null = null, groupKey: string | null = null) => {
      setOpenGroupKey(null) // for next time, start with null
      showSelector(null)
      if (isFunction(onSelect)) onSelect(itemKey, groupKey)
    },
    [onSelect]
  ) // the callback only changes if the handler changes

  // install a handler to close on "Esc" key, run on every render
  useEffect(getEscHandler(close))

  // validations
  if (!isArrayWithItems(options)) {
    log.error(
      "Missing options for selector. Expected: one or more ItemGroup, Got:",
      options,
      "typeof:",
      typeof options
    )
    return <></> //nothing to do
  }

  const isThereOnlyOneItemInOptions: boolean = options.every(
    (item) => item.items.length === 1
  )

  // if options has only 1 group, then show the items as a list instead of an accordian
  const isAccordian: boolean = options.length > 1
  const topLevel = isAccordian ? options : options[0].items

  // within options, find the currently selected group
  const openGroup = options.find((group) => {
    // if selectedGroup was specified, then match that
    // otherwise check whether any of this group's items match the selectedKey
    return isNotNull(selectedGroup) ?
        group.key == selectedGroup
      : isNotNull(selectedKey) &&
          group.items.some((item) => item.key == selectedKey)
  })
  // if its accordian, set the key of the group found above, or if its list then use selectedKey
  const topLevelSelectedKey = isAccordian ? openGroup?.key : selectedKey

  // if accordian and opening for the first time, set the topLevelSelectedKey as the openGroupKey
  if (isAccordian && isNull(openGroupKey) && topLevelSelectedKey) {
    setOpenGroupKey(topLevelSelectedKey)
    return <></> // return right away so we can re-render
  }

  return (
    <PopupComponent
      setOpenGroupKey={setOpenGroupKey}
      openGroupKey={openGroupKey}
      topLevel={topLevel}
      topLevelSelectedKey={topLevelSelectedKey}
      close={close}
      isAccordian={isAccordian}
      selectedKey={selectedKey}
      isThereOnlyOneItemInOptions={isThereOnlyOneItemInOptions}
      header={header}
      footer={footer}
    />
  )
}
